
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Layout from './components/Layout';
import { DataProvider, useData } from './context/DataContext';
import { SubscriptionLockout } from './components/SubscriptionLockout';
import { useExpenseReminders } from './src/hooks/useExpenseReminders';
import { useCriticalStockAlert } from './src/hooks/useCriticalStockAlert';
import { useIsPublicRoute } from './src/hooks/useIsPublicRoute';

const Login       = React.lazy(() => import('./pages/Login'));
const VerifyPhone = React.lazy(() => import('./pages/VerifyPhone'));
const Setup       = React.lazy(() => import('./pages/Setup'));
const PublicMenu  = React.lazy(() => import('./pages/PublicMenu'));
const PendingPayments = React.lazy(() => import('./pages/PendingPayments'));
const Dashboard   = React.lazy(() => import('./pages/Dashboard'));
const Tables      = React.lazy(() => import('./pages/Tables'));
const POS         = React.lazy(() => import('./pages/POS'));
const Payment     = React.lazy(() => import('./pages/Payment'));
const Menu        = React.lazy(() => import('./pages/Menu'));
const Stock       = React.lazy(() => import('./pages/Stock'));
const Loyalty     = React.lazy(() => import('./pages/Loyalty'));
const QrMenu      = React.lazy(() => import('./pages/QrMenu'));
const StaffPage   = React.lazy(() => import('./pages/Staff'));
const Reports     = React.lazy(() => import('./pages/Reports'));
const Settings    = React.lazy(() => import('./pages/Settings'));
const ExpenseSettings = React.lazy(() => import('./pages/ExpenseSettings'));

const PageLoader = () => (
  <div className="h-screen w-full flex items-center justify-center bg-[rgb(var(--color-loader-bg))]">
    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

// Initialize Google Analytics
ReactGA.initialize("G-LMP9EFZ3YK");

const AnalyticsTracker = () => {
  const location = useLocation();

  React.useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
  }, [location]);

  return null;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { cloudUser, isAuthReady, appSettings } = useData();
  if (!isAuthReady) return null;
  if (!cloudUser) return <Navigate to="/login" replace />;
  if (!appSettings.isSetupComplete) return <Navigate to="/setup" replace />;
  return <>{children}</>;
};

const SetupRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { cloudUser, isAuthReady, appSettings } = useData();
  if (!isAuthReady) return null;
  if (!cloudUser) return <Navigate to="/login" replace />;
  if (!cloudUser.phoneNumber) return <Navigate to="/verify-phone" replace />;
  if (appSettings.isSetupComplete) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const VerifyPhoneRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { cloudUser, isAuthReady } = useData();
  if (!isAuthReady) return null;
  if (!cloudUser) return <Navigate to="/login" replace />;
  if (cloudUser.phoneNumber) return <Navigate to="/setup" replace />;
  return <>{children}</>;
};

const ADMIN_EMAILS = ['tuncergul.designer@gmail.com', 'info@kahveinn.com'];

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { cloudUser, isAuthReady } = useData();
  if (!isAuthReady) return null;
  if (!cloudUser) return <Navigate to="/login" replace />;
  if (!cloudUser.email || !ADMIN_EMAILS.includes(cloudUser.email)) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const ExpenseReminderCheck: React.FC = () => {
  useExpenseReminders();
  return null;
};

const CriticalStockAlertCheck: React.FC = () => {
  useCriticalStockAlert();
  return null;
};

// Musteri-facing rotalar (QR menu, login) kafenin abonelik durumundan ve admin
// bildirimlerinden tamamen bagimsiz olmali - bunlar orada ASLA gosterilmemeli.
const ConditionalSubscriptionLockout: React.FC = () => {
  const isPublicRoute = useIsPublicRoute();
  if (isPublicRoute) return null;
  return <SubscriptionLockout />;
};

const ConditionalAdminNotifications: React.FC = () => {
  const isPublicRoute = useIsPublicRoute();
  if (isPublicRoute) return null;
  return (
    <>
      <ExpenseReminderCheck />
      <CriticalStockAlertCheck />
    </>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ConditionalAdminNotifications />
      <ConditionalSubscriptionLockout />
      <AnalyticsTracker />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/verify-phone" element={<VerifyPhoneRoute><VerifyPhone /></VerifyPhoneRoute>} />
          <Route path="/setup" element={<SetupRoute><Setup /></SetupRoute>} />
          <Route path="/m/:slug" element={<PublicMenu />} />
          <Route path="/admin/pending-payments" element={<AdminRoute><PendingPayments /></AdminRoute>} />

          <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
          <Route path="/tables" element={<ProtectedRoute><Layout><Tables /></Layout></ProtectedRoute>} />
          <Route path="/pos/:id" element={<ProtectedRoute><Layout><POS /></Layout></ProtectedRoute>} />
          <Route path="/payment/:id?" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
          <Route path="/menu" element={<ProtectedRoute><Layout><Menu /></Layout></ProtectedRoute>} />
          <Route path="/stock" element={<ProtectedRoute><Layout><Stock /></Layout></ProtectedRoute>} />
          <Route path="/loyalty" element={<ProtectedRoute><Layout><Loyalty /></Layout></ProtectedRoute>} />
          <Route path="/qr-menu" element={<ProtectedRoute><Layout><QrMenu /></Layout></ProtectedRoute>} />
          <Route path="/staff" element={<ProtectedRoute><Layout><StaffPage /></Layout></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><Layout><Reports /></Layout></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
          <Route path="/settings/expenses" element={<ProtectedRoute><Layout><ExpenseSettings /></Layout></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
